import './Signin.css';
import AuthLeftPanel from "../../../User/Auth/LeftAuthPanel/AuthLeftPanel";
import Footer from "../../../../Components/Footer/Footer";
import { Button, Card, Form } from "react-bootstrap";
import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from "axios";
import { toast } from 'react-hot-toast';
import Loading from 'react-fullscreen-loading';
import TurnstileCaptcha from '../../../../Components/Captcha/TurnstileCaptcha';
import logo from '../../../../assets/Logo/cadomationnewlogo.webp';
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useAuth } from '../../../../authentication/AuthContext';
import { apiErrorMessage } from '../../../../utils/apiErrors';
import { saveCredential } from '../../../../utils/saveCredential';
import { returnPathFromSearch } from '../../../../utils/returnTo';

export default function Signin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setSession } = useAuth();
  // `?next=` is set by UserRoute / AuthContext when a signed-out visitor hit a
  // customer page — e.g. the renewal email's deep link. Validated in
  // utils/returnTo so it can only ever be an in-app path.
  const returnTo = returnPathFromSearch(location.search);
  const [screenloader, setScreenloader] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const captchaRef = useRef(null);
  const [captchaLoaded, setCaptchaLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkScreen = () => setIsMobile(window.innerWidth <= 600);
    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  const login = async () => {
    await axios.post(`${process.env.REACT_APP_API_URL}/auth/signin`, { email, password, recaptchaToken: recaptchaValue })
      .then(async res => {
        setScreenloader(false);
        if (res.data.status === true) {
          toast.success("Login Successful!");
          localStorage.setItem("accessToken", res.headers['access-token']);
          localStorage.setItem("refreshToken", res.headers['refresh-token']);
          localStorage.setItem("userType", res.data.userType);
          if (res.data.quote_requested_at) {
            localStorage.setItem("quote_requested_at", res.data.quote_requested_at);
          }
          localStorage.setItem("email", email);
          // Install the session synchronously so the guard sees "ready" with
          // the correct userType AND permission list before we navigate —
          // otherwise it reads the pre-login "anon" state and bounces straight
          // back to /signin, or renders the sidebar with nothing in it.
          // A successful sign-in is the browser's cue to offer to save the
          // password — and to UPDATE a stored one when the user has just
          // signed in with something different from what it remembers.
          // Awaited before navigating: the prompt is dropped if the tab moves
          // on first, which is what made this unreliable in an SPA.
          await saveCredential({ email, password });

          setSession({
            userType: res.data.userType,
            role: res.data.role,
            roleName: res.data.roleName,
            permissions: res.data.permissions,
            // An unverified or half-filled account signs in normally — the
            // dashboard's setup card is what prompts them to finish, not a
            // locked door here.
            verified: res.data.verified,
            profileComplete: res.data.profileComplete,
            email,
          });
          if (res.data.userType === "user") {
            navigate(returnTo || `/User/Dashboard`, { replace: true });
          } else if (res.data.userType === "reseller") {
            navigate(`/Admin/ResellerDashboard`, { replace: true });
          } else {
            navigate(`/Admin/Dashboard`, { replace: true });
          }
          return;
        } else {
          console.log(res.data)
          toast.error(res.data.message);
          resetCaptcha();
          return;
        }
      })
      .catch(error => {
        setScreenloader(false);
        console.log(error?.response?.data);
        toast.error(apiErrorMessage(error, "An unexpected error occurred."));
        resetCaptcha();
        return;
      });

  };
  const resetCaptcha = () => {
    if (captchaRef.current && typeof captchaRef.current.reset === 'function') {
      captchaRef.current.reset();
    }
    setRecaptchaValue(null);
  }

  const Submit = async (e) => {
    e.preventDefault();
    if (!captchaLoaded) {
      toast.error("Captcha is not loaded yet. Please wait.");
      return;
    }
    if (!recaptchaValue) {
      toast.error("Please verify you're not a robot.");
      return;
    }

    if (email.length > 100) {
      toast.error("Email exceeds the maximum length of 50 characters.");
      return;
    }
    if (password.length > 100) {
      toast.error("Password exceeds the maximum length of 50 characters.");
      return;
    }
    setScreenloader(true);
    await login();
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleTurnstileVerify = (value) => {
    setRecaptchaValue(value);
  };

  return (
    <>
      <Loading loading={screenloader} background="#fff" loaderColor="#883484" />
      <div className="signin-page">
        <div className="auth-wrapper">
          <AuthLeftPanel />
          <div className="right-panel">
            <Card className="signin-card">
              <Card.Body>
                <div className="logo-wrapper text-center mb-4">
                  <img src={logo} alt="Cadomation" className="signin-logo" />
                </div>
                <h4 className="form-title text-center">Sign In to your account</h4>
                <p className="form-sub text-center">
                  Welcome back! Please enter your details
                </p>
                <Form onSubmit={e => Submit(e)}>
                  <Form.Group className="mb-3" controlId="email">
                    <Form.Control
                      type="email"
                      name="email"
                      autoComplete="username"
                      placeholder="e.g username@gmail.com"
                      required
                      maxLength={100}
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className="form-input"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3" controlId="password">
                    <div className="position-relative">
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        name="password"
                        autoComplete="current-password"
                        placeholder="Password"
                        required
                        maxLength={100}
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        className="form-input pe-5"
                      />

                      <span
                        className="password-toggle"
                        onClick={toggleShowPassword}
                        role="button"
                        tabIndex={0}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </span>
                    </div>
                  </Form.Group>
                  <div className="remember-me-label mb-3 d-flex justify-content-between align-items-center">
                    <Form.Check
                      type="checkbox"
                      label="Remember me"
                      style={{ color: "#111827" }}
                    />
                    <span
                      className="forgot-password-link"
                      onClick={() => navigate(`/forgotpasswordrequest`, { replace: true })}
                      role="button"
                      tabIndex={0}
                    >
                      Forgot password?
                    </span>
                  </div>
                  <TurnstileCaptcha
                    ref={captchaRef}
                    onVerify={handleTurnstileVerify}
                    onExpire={() => setRecaptchaValue(null)}
                    onLoad={() => setCaptchaLoaded(true)}
                  />
                  <div className="d-grid">
                    <Button
                      type="submit"
                      className=" btn-signin"
                    >
                      Sign In
                    </Button>
                  </div>
                </Form>
                <p className="text-center mt-3 mb-0">
                  Don't have an account?{" "}
                  <span
                    className="sign-up-link nowrap"
                    onClick={() => navigate(`/signup`, { replace: true })}
                    role="button"
                    tabIndex={0}
                  >
                    Sign up
                  </span>
                </p>
              </Card.Body>
              {isMobile && <Footer />}
            </Card>
          </div>
        </div>
        {!isMobile && <Footer />}
      </div>
    </>
  );
}
